/* MÂZIA header area (spec 01 §2-4.3). Loaded with defer by sections/mz-header.liquid, after assets/mz-theme.js.
   - Announcement bar: phones rotate one message every few seconds (cross-fade; with reduced motion an instant
     swap and one round only), held while hovered, touched or focused; desktop shows the row and hides trailing
     messages that would not fit. Messages are read afresh: one that leaves an open page (the Diwali promise's,
     assets/mz-theme.js) hands over to the next at once.
   - Masthead (desktop): paints the MAZIA layer every scroll frame from the design's maths and adds the
     header hairline (.is-scrolled) and .is-docked; the phone home logo fades in once the big logo has
     scrolled under the header.
   - Bag: keeps "Bag (n)" and the phone bag's accessible name in sync with Horizon's cart events
     (the phone badge itself is Horizon's <cart-icon>), and the triggers' aria-expanded with the drawer.
   - Phone menu (Horizon's <header-drawer>): closes on desktop, when another sheet opens and when one of its
     links is followed (Account included, which Magic SSO takes over), makes the page behind it inert,
     focuses the close button on open and the menu button on close, emits mz:sheet-open { id: 'menu' };
     finishes opening a menu that was tapped open before the scripts ran.
   - Search overlay (Horizon's dialog + predictive search): placeholder per layout, popular-search pills (the
     focus goes back to the field on desktop, to the count line on phones), data-state (empty | results | none),
     the count line, the status line screen readers hear, the no-results label and "View all [count] results";
     emits mz:sheet-open { id: 'search' }.
   Classes on <html>: mz-menu-open, mz-search-open (for floating UI that hides behind sheets). */
(() => {
  if (window.mzHeader) return;
  window.mzHeader = true;

  const root = document.documentElement;
  const desktop = window.matchMedia('(min-width: 750px)');
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(`mz:${name}`, { detail }));
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Announcement bar ---------- */

  function initAnnouncement(bar) {
    if (!bar || bar.mzReady) return;
    bar.mzReady = true;

    // Read afresh every time: messages can leave an open page (assets/mz-theme.js takes the Diwali promise's
    // "order by" message out when that day ends, and its other message after the promise's last day)
    const messagesNow = () => [...bar.querySelectorAll('[data-mz-announce-msg]')];
    const interval = Math.max(1000, Number(bar.dataset.interval) || 3800);
    let index = 0;
    let current = null;
    let timer = 0;
    let ticks = 0;
    let stopped = false;
    // Held still while the theme editor has a message selected, and while the bar is hovered, touched or has
    // the focus (WCAG 2.2.2); the next message then comes a full interval after it is let go.
    const holds = new Set();

    const show = (next) => {
      const messages = messagesNow();
      if (!messages.length) return;
      index = ((next % messages.length) + messages.length) % messages.length;
      current = messages[index];
      messages.forEach((message, i) => {
        message.classList.toggle('is-current', i === index);
        if (desktop.matches) message.removeAttribute('aria-hidden');
        else message.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });
    };

    // Desktop: drop trailing messages (and the dot before each) until the row fits.
    const fit = () => {
      const items = [...bar.children];
      items.forEach((item) => item.classList.remove('is-cut'));
      if (!desktop.matches) return;
      for (let i = items.length - 1; i > 1 && bar.scrollWidth > bar.clientWidth + 1; i -= 1) {
        const item = items[i];
        if (!item.matches('[data-mz-announce-msg]') || getComputedStyle(item).display === 'none') continue;
        item.classList.add('is-cut');
        items[i - 1]?.classList.add('is-cut');
      }
    };

    const tick = () => {
      const messages = messagesNow();
      if (holds.size || document.hidden || desktop.matches || messages.length < 2) return;
      // The one after the shown message (from its place now, if earlier messages left the bar); if the shown
      // one left, the one that took its place
      const at = messages.indexOf(current);
      show(at < 0 ? index : at + 1);
      ticks += 1;
      // Reduced motion: one round of the messages, then the bar rests on the first one
      if (reducedMotion() && ticks >= messages.length && index === 0) {
        stopped = true;
        clearInterval(timer);
      }
    };

    // A message that leaves while it is shown: the one now in its place shows at once (never an empty bar);
    // the desktop row is fitted again
    if ('MutationObserver' in window) {
      new MutationObserver(() => {
        if (current && !bar.contains(current)) show(index);
        fit();
      }).observe(bar, { childList: true });
    }

    const restart = () => {
      clearInterval(timer);
      if (!stopped) timer = setInterval(tick, interval);
    };

    const hold = (reason, on) => {
      if (on) holds.add(reason);
      else if (holds.delete(reason) && !holds.size) restart();
    };

    const sync = () => {
      show(index);
      fit();
    };

    restart();
    desktop.addEventListener('change', sync);
    window.addEventListener('resize', () => requestAnimationFrame(fit), { passive: true });
    document.fonts?.ready?.then(fit);
    sync();

    // Hover, touch (a touch pointer enters on contact and leaves once lifted or scrolling) and focus
    bar.addEventListener('pointerenter', () => hold('pointer', true));
    bar.addEventListener('pointerleave', () => hold('pointer', false));
    bar.addEventListener('focusin', () => hold('focus', true));
    bar.addEventListener('focusout', (event) => {
      if (!bar.contains(event.relatedTarget)) hold('focus', false);
    });

    // Theme editor: show the selected message and hold it there
    bar.addEventListener('shopify:block:select', (event) => {
      const i = messagesNow().findIndex((message) => message.contains(event.target) || message === event.target);
      if (i < 0) return;
      hold('editor', true);
      show(i);
    });
    bar.addEventListener('shopify:block:deselect', () => hold('editor', false));
  }

  /* ---------- Masthead, header hairline, phone logo fade ---------- */

  // Until the fonts are measured: the design's widths per 1px of font-size (MÂZIA 2.633, MÂZIA HOME 5.256)
  const chrome = { raf: 0, geometry: null, ratios: { mark: 2.633, ref: 5.256 } };

  function measureText(mark) {
    // Widths per 1px of font-size, in the font actually shown, of the wordmark's own text and of the text its
    // big size is taken from (data-mz-size-ref, the masthead's "Big size reference" setting; blank = the
    // wordmark itself).
    const own = mark.textContent.trim();
    const reference = (mark.dataset.mzSizeRef || '').trim() || own;
    const probe = mark.cloneNode(false);
    probe.removeAttribute('data-mz-mark');
    probe.classList.remove('is-live');
    probe.style.cssText =
      'display:block;position:absolute;top:0;left:-9999px;visibility:hidden;transform:none;font-size:100px;will-change:auto;';
    document.body.append(probe);
    probe.textContent = reference;
    const ref = probe.getBoundingClientRect().width;
    probe.textContent = own;
    const width = probe.getBoundingClientRect().width;
    probe.remove();
    if (ref > 0 && width > 0) chrome.ratios = { mark: width / 100, ref: ref / 100 };
  }

  function geometry(mast, mark) {
    const vw = root.clientWidth;
    const gutter = parseFloat(getComputedStyle(mark).left) || 32;
    const content = vw - 2 * gutter;
    const { ratios } = chrome;
    const fsBig = content / ratios.ref;
    const fsSmall = Math.min(44, 176 / ratios.ref);
    const x0 = Math.max(0, (content - ratios.mark * fsBig) / 2);
    const S = 104 + 0.78 * fsBig + 32;
    chrome.geometry = { fsBig, fsSmall, x0, S };
    const spacer = mast.querySelector('[data-mz-mast-spacer]');
    if (spacer) spacer.style.height = `${Math.max(0, Math.round(S) - 96)}px`;
  }

  function paint() {
    chrome.raf = 0;
    const header = document.querySelector('[data-mz-header]');
    const mast = document.querySelector('[data-mz-mast]');
    const mark = mast?.querySelector('[data-mz-mark]');
    const big = mast?.dataset.big === 'true';
    const y = window.scrollY || root.scrollTop || 0;

    if (!desktop.matches) {
      header?.classList.remove('is-scrolled');
      return;
    }
    if (!mark) {
      header?.classList.toggle('is-scrolled', y > 4);
      header?.classList.add('is-docked');
      return;
    }
    if (!chrome.geometry) geometry(mast, mark);

    const g = chrome.geometry;
    const p = big ? Math.min(1, Math.max(0, y / (g.S - 96))) : 1;
    // Docked: the wordmark sits in the header over the home link (mz-header.css draws the link's focus ring
    // round the wordmark, big or docked)
    header?.classList.toggle('is-docked', p >= 1);
    const size = g.fsBig + (g.fsSmall - g.fsBig) * p;
    const top = 104 + (32 + (64 - 0.78 * g.fsSmall) / 2 - 104) * p;
    mark.style.fontSize = `${size.toFixed(2)}px`;
    mark.style.transform = `translate3d(${(g.x0 * (1 - p)).toFixed(2)}px, ${top.toFixed(2)}px, 0)`;
    mark.classList.add('is-live');
    header?.classList.toggle('is-scrolled', big ? y > g.S - 98 : y > 4);
  }

  const requestPaint = () => {
    if (!chrome.raf) chrome.raf = requestAnimationFrame(paint);
  };

  const relayout = () => {
    chrome.geometry = null;
    requestPaint();
  };

  function initMasthead() {
    const mast = document.querySelector('[data-mz-mast]');
    const mark = mast?.querySelector('[data-mz-mark]');
    // Again for a section the theme editor re-renders (the text or the size reference may have changed)
    if (mark) {
      document.fonts?.ready?.then(() => {
        if (!mark.isConnected) return;
        measureText(mark);
        relayout();
      });
    }
    relayout();
    paint();

    // Phone home: the header logo appears once the big logo is fully under the 52px header
    const logo = document.querySelector('[data-mz-header-logo]');
    const mastPhone = document.querySelector('[data-mz-mast-m]');
    chrome.observer?.disconnect();
    if (!logo) return;
    if (!mastPhone || !('IntersectionObserver' in window)) {
      logo.classList.add('is-shown');
      return;
    }
    chrome.observer = new IntersectionObserver(
      ([entry]) => logo.classList.toggle('is-shown', !entry.isIntersecting),
      { rootMargin: '-52px 0px 0px 0px' }
    );
    chrome.observer.observe(mastPhone);
  }

  window.addEventListener('scroll', requestPaint, { passive: true });
  window.addEventListener('resize', relayout, { passive: true });
  desktop.addEventListener('change', relayout);

  /* ---------- Bag count ---------- */

  function setBagCount(count) {
    if (!Number.isInteger(count) || count < 0) return;
    for (const node of document.querySelectorAll('[data-mz-cart-count]')) {
      if (node.textContent !== String(count)) node.textContent = String(count);
    }
    for (const trigger of document.querySelectorAll('[data-mz-bag-label]')) {
      const { mzBagLabel: label = 'Bag', mzPiecesOne: one = '1 piece', mzPiecesOther: other = '[count] pieces' } =
        trigger.dataset;
      const pieces = count === 1 ? one : other.replace('[count]', String(count));
      trigger.setAttribute('aria-label', count > 0 ? `${label}, ${pieces}` : label);
    }
  }

  document.addEventListener('shopify:cart:lines-update', (event) => {
    event.promise
      ?.then((result) => {
        const count = result?.cart?.totalQuantity ?? result?.detail?.itemCount;
        if (Number.isInteger(count)) setBagCount(count);
      })
      .catch(() => {});
  });

  // Back/forward cache: the cart may have changed on another page
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    const base = window.Shopify?.routes?.root || '/';
    fetch(`${base}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((cart) => cart && setBagCount(cart.item_count))
      .catch(() => {});
  });

  // Horizon's <cart-icon> keeps a fresh count in sessionStorage when a cached page is shown
  try {
    const stored = JSON.parse(sessionStorage.getItem('cart-count') || 'null');
    if (stored && Date.now() - stored.timestamp < 10000) setBagCount(parseInt(stored.value, 10));
  } catch (error) {
    /* no storage */
  }

  // mz:sheet-open { id: 'bag' } comes from assets/mz-cart.js (loaded with the bag drawer), not from here
  const syncBagExpanded = (event) => {
    if (event.target?.id !== 'cart-drawer') return;
    const open = event.type === 'theme-drawer:open';
    for (const trigger of document.querySelectorAll('[aria-controls="cart-drawer"]')) {
      trigger.setAttribute('aria-expanded', String(open));
    }
  };
  document.addEventListener('theme-drawer:open', syncBagExpanded);
  document.addEventListener('theme-drawer:close', syncBagExpanded);

  /* ---------- Phone menu ---------- */

  // While the menu is open the page behind it is inert, so screen readers stay in the panel (header-drawer.js
  // keeps Tab there): every sibling of the panel and of its ancestors up to .page-wrapper (the rest of the
  // header, the bar, the masthead, <main>, the footer) and the skip link. The scrim stays live (a tap closes
  // the menu), and dialogs and the layers apps add to <body> are left alone.
  let inertNodes = [];

  function setPageInert(panel, on) {
    inertNodes.forEach((node) => {
      node.inert = false;
    });
    inertNodes = [];
    if (!on || !panel?.isConnected) return;
    const stop = panel.closest('.page-wrapper') || document.body;
    const candidates = [document.querySelector('body > .skip-to-content-link')];
    for (let node = panel; node && node !== stop && node.parentElement; node = node.parentElement) {
      for (const sibling of node.parentElement.children) {
        if (sibling !== node) candidates.push(sibling);
      }
    }
    for (const node of candidates) {
      if (!(node instanceof HTMLElement) || node.inert) continue;
      if (node.matches('script, style, link, template, dialog, dialog-component, .mz-menu__scrim')) continue;
      node.inert = true;
      inertNodes.push(node);
    }
  }

  function initMenu() {
    const drawer = document.querySelector('header-drawer.mz-menu');
    const details = drawer?.querySelector('details');
    if (!details || details.mzReady) return;
    details.mzReady = true;
    const summary = details.querySelector('summary');
    const close = details.querySelector('.mz-menu__close');
    const panel = details.querySelector('.mz-menu__panel');
    let shown = false;

    const onToggle = (event) => {
      if (details.open === shown) return;
      shown = details.open;
      root.classList.toggle('mz-menu-open', shown);
      if (shown) {
        // Every open starts at the top (the panel keeps its scroll position while closed); not a menu opened
        // before this script ran, which the shopper may be scrolling already
        if (event && panel) panel.scrollTop = 0;
        setPageInert(panel, true);
        emit('sheet-open', { id: 'menu' });
        // After the 200ms entrance (header-drawer.js traps focus then)
        setTimeout(() => {
          if (details.open && !panel?.contains(document.activeElement)) {
            close?.focus({ preventScroll: true });
          }
        }, reducedMotion() ? 0 : 220);
      } else {
        setPageInert(panel, false);
        details.classList.remove('is-instant');
        emit('sheet-close', { id: 'menu' });
        const active = document.activeElement;
        if (!active || active === document.body || details.contains(active)) summary?.focus({ preventScroll: true });
      }
    };
    details.addEventListener('toggle', onToggle);

    // header-drawer.js's focus trap ends by focusing its container, the panel (a scroll box, which Chrome lets
    // take the focus): hand it on to Close, so the open menu starts there as a dialog does
    panel?.addEventListener('focus', () => {
      if (details.open) close?.focus({ preventScroll: true });
    });

    if (!details.open) {
      // (a section the theme editor re-rendered while its menu was open)
      setPageInert(null, false);
      return;
    }

    // Opened by a tap before the scripts had run (slow phones: the <details> opens natively and mz-header.css
    // shows it): finish opening it as a tap does now. Page inert, html.mz-menu-open, focus on Close; then, once
    // header-drawer.js has run, its aria-expanded and focus trap (no entrance slide: the panel is already on
    // screen) and dialog.js's page scroll lock (it missed the toggle event).
    onToggle();
    customElements.whenDefined('header-drawer').then(() => {
      if (!details.open || details.classList.contains('menu-open')) return;
      details.classList.add('is-instant');
      drawer.open?.();
      if (!root.hasAttribute('scroll-lock')) details.dispatchEvent(new Event('toggle'));
    });
  }

  const closeMenu = () => {
    const drawer = document.querySelector('header-drawer.mz-menu');
    if (drawer?.querySelector('details')?.open) drawer.close?.();
  };

  // Following a menu link closes the menu first, so the back button returns to a closed menu. On the window in
  // the capture phase: Razorpay Magic SSO stops clicks on account links there (it registers later, after
  // loading its config), and its login should not open over a menu that still traps the focus.
  window.addEventListener(
    'click',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('.mz-menu__panel a[href]') : null;
      if (!link || link.target === '_blank' || /^(tel|mailto|sms):$/.test(link.protocol)) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      closeMenu();
    },
    true
  );

  desktop.addEventListener('change', (event) => {
    if (event.matches) closeMenu();
  });

  /* ---------- Search overlay ---------- */

  function initSearch() {
    const modal = document.getElementById('search-modal');
    if (!modal || modal.mzReady || !modal.matches('[data-mz-search]')) return;
    modal.mzReady = true;

    const dialog = modal.querySelector('dialog');
    const form = modal.querySelector('form');
    const input = modal.querySelector('.mz-search__input');
    const results = modal.querySelector('[data-mz-search-results]');
    const count = modal.querySelector('[data-mz-search-count]');
    const status = modal.querySelector('[data-mz-search-status]');
    const noneLabel = modal.querySelector('[data-mz-search-none-label]');
    const all = modal.querySelector('[data-mz-search-all]');
    const body = modal.querySelector('.mz-search__body');
    if (!dialog || !input || !results) return;

    // "View all 79 results": the search page for the query on screen, when the store found more products than
    // the list shows (phones 30 rows, from 750px the first 24 cards). The same URL as the form's (Enter on
    // desktop): /search?q=…&type=product&options[prefix]=last.
    const updateAll = (state, data) => {
      if (!all) return;
      const total = data ? parseInt(data.dataset.mzResults, 10) || 0 : 0;
      const shown = data ? Math.min(data.querySelectorAll('.mz-sr-item').length, desktop.matches ? 24 : 30) : 0;
      all.hidden = state !== 'results' || total <= shown;
      if (all.hidden) return;
      const url = new URL(form?.action || '/search', location.origin);
      const params = form ? new URLSearchParams(new FormData(form)) : new URLSearchParams();
      params.set('q', data.dataset.mzTerms || input.value.trim());
      url.search = params.toString();
      all.href = url.pathname + url.search;
      all.textContent = (all.dataset.template || '[count]').replace('[count]', String(total));
    };

    const setPlaceholder = () => {
      const text = desktop.matches ? input.dataset.placeholderDesktop : input.dataset.placeholderMobile;
      if (text != null) input.placeholder = text;
    };
    setPlaceholder();
    desktop.addEventListener('change', setPlaceholder);

    // A popular search picked on a phone, waiting for its results (see the pills below)
    let pickPending = false;

    // The status line (visually hidden, role=status) tells screen readers what the search found, in every
    // state and at every width (the count line is hidden on phones when nothing is found). It is written only
    // when the message changes, so the several updates of one render are announced once, and emptied with the
    // field, so the same search made again is announced again. quiet: the count line has just taken the focus
    // and is read out with it.
    let spoken = '';
    const announce = (message, quiet = false) => {
      if (message === spoken) return;
      spoken = message;
      if (status) status.textContent = quiet ? '' : message;
    };

    let lastResults = null;
    const update = () => {
      const data = results.querySelector('[data-mz-results]');
      const query = input.value.trim();
      let state = 'empty';
      // For the status line: '' before a search, null while the first request is still running
      let message = '';
      if (query && data) {
        const total = parseInt(data.dataset.mzResults, 10) || 0;
        state = total > 0 ? 'results' : 'none';
        const template = (total === 1 ? count?.dataset.one : count?.dataset.other) || '[count]';
        message = template.replace('[count]', String(total));
        if (count) count.textContent = message;
        if (noneLabel) {
          noneLabel.textContent = (noneLabel.dataset.template || '').replace('[query]', data.dataset.mzTerms || query);
          // Phones show this label ("No results for “zzqx”. Try") instead of the count line
          const none = noneLabel.textContent.trim();
          if (state === 'none' && !desktop.matches && none) message = none;
        }
      } else if (query) {
        // First request still running: keep what is on screen
        state = modal.dataset.state || 'empty';
        message = null;
      }
      results.removeAttribute('aria-busy');
      modal.dataset.state = state;
      // Phones, after a popular search: the results hide the pills, and the focus with them. It goes to the
      // count line ("78 results"), just above the results, unless the shopper has put it somewhere else since.
      // (No results: the pills stay on screen under "No results for …", and so does the focus.)
      let focused = false;
      if (pickPending && state !== 'empty') {
        pickPending = false;
        const active = document.activeElement;
        const lost = !active || active === document.body || active === dialog || !!active.closest('[data-mz-search-empty]');
        if (state === 'results' && lost && dialog.open && count) {
          count.focus({ preventScroll: true });
          focused = true;
        }
      }
      if (message !== null) announce(message, focused);
      updateAll(state, query ? data : null);
      if (data && data !== lastResults) {
        lastResults = data;
        (desktop.matches ? dialog : body)?.scrollTo?.(0, 0);
      }
    };

    new MutationObserver(update).observe(results, { childList: true, subtree: true });
    desktop.addEventListener('change', update);

    input.addEventListener('input', () => {
      if (!input.value.trim()) {
        modal.dataset.state = 'empty';
        updateAll('empty', null);
        announce('');
      } else {
        results.setAttribute('aria-busy', 'true');
      }
    });

    // Popular searches: put the text in the field and search (no navigation). Desktop: the focus goes back to
    // the field. Phones: the field would raise the keyboard over the results, so update() moves the focus to
    // the count line once they are in.
    modal.addEventListener('click', (event) => {
      const pill = event.target instanceof Element ? event.target.closest('[data-mz-search-pick]') : null;
      if (!pill) return;
      event.preventDefault();
      input.value = pill.dataset.mzSearchPick || pill.textContent.trim();
      pickPending = !desktop.matches;
      input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertReplacementText', data: input.value }));
      if (desktop.matches) input.focus({ preventScroll: true });
    });

    modal.addEventListener('dialog:open', () => {
      root.classList.add('mz-search-open');
      emit('sheet-open', { id: 'search' });
      closeMenu();
      const cart = document.getElementById('cart-drawer');
      if (cart?.hasAttribute('open')) cart.close?.();
      setPlaceholder();
      update();
    });

    modal.addEventListener('dialog:close', () => {
      root.classList.remove('mz-search-open');
      emit('sheet-close', { id: 'search' });
    });

    // Escape while the field is blurred (phones blur it on Enter) reaches the native <dialog> instead of
    // dialog.js: close through dialog.js so the page scroll is unlocked.
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      modal.closeDialog?.();
    });
    dialog.addEventListener('close', () => root.classList.remove('mz-search-open'));

    // Opened before this script ran (assets/dialog.js runs first, and sections/mz-header.liquid replays a tap on
    // Search made while the page was loading): catch up with the open overlay
    if (dialog.open) root.classList.add('mz-search-open');
    update();
  }

  // One sheet at a time: another sheet (wishlist, bag, quick add) closes the menu and the search
  document.addEventListener('mz:sheet-open', (event) => {
    const id = event.detail?.id;
    if (id !== 'menu') closeMenu();
    if (id !== 'search') {
      const modal = document.getElementById('search-modal');
      if (modal?.refs?.dialog?.open) modal.closeDialog?.();
    }
  });

  /* ---------- Start (and again for sections the theme editor re-renders) ---------- */

  function init() {
    document.querySelectorAll('[data-mz-announce]').forEach(initAnnouncement);
    initMasthead();
    initMenu();
    initSearch();
  }

  init();
  document.addEventListener('shopify:section:load', init);
})();
